import { AdminRoleId, DeclineReasonCharEnum } from '@tassat/common-definitions';
import { UserRecord } from '@tassat/fred-definitions';
import { v4 as uuidv4 } from 'uuid';

const adminRequestMapping = {
    'GET-adminUsers': {
        requestPattern: 'adminUsers',
        method: 'GET',
        permittedRoles: [AdminRoleId.HELP_DESK]
    },
    'GET-users': {
        requestPattern: 'users',
        method: 'GET',
        permittedRoles: [
            AdminRoleId.API_OPS,
            AdminRoleId.BANK_OPS,
            AdminRoleId.BANK_OPS_SUPERVISOR,
            AdminRoleId.CLIENT_SERVICES,
            AdminRoleId.PCG_ADMIN
        ]
    },
    'GET-firms': {
        requestPattern: 'firms',
        method: 'GET',
        permittedRoles: [
            AdminRoleId.BANK_OPS,
            AdminRoleId.CLIENT_SERVICES,
            AdminRoleId.PCG_ADMIN
        ]
    }
};

export class Messaging {
    socket: any;
    hook: any;
    promises = {};

    constructor(socket, hook) {
        this.socket = socket;
        this.hook = hook;
        this.socket.setEventListener(this.onMessage);
    }

    onMessage = (msg) => {
        console.debug('Received in messaging', msg);
        if (this.promises[msg.UUID])
            this.promises[msg.UUID].resolve(msg);
        else {
            this.hook.postMessage({
                type: 'data',
                response: msg
            });
        }
    };

    tokenUpdate(token) {
        return this.request('PUT', 'tokenupdate', { Token: token });
    }

    createUser(firmId, userValues) {
        return this.request('POST', 'users', {
            ...userValues,
            FirmId: firmId
        });
    }

    updateSettlementInstruction(values) {
        return this.request('PUT', 'settlement_instructions', { ...values });
    }

    updateUserAccount(values) {
        return this.request('PUT', 'useraccount', { ...values });
    }

    getOrders({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'order',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    getExecutions({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'execution',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    // get the linked request id for transactions made with a pay now rfp
    getLinkedRequestId({ ContainerId }) {
        return this.request('GET', 'getLinkedRequestId', { ContainerId });
    }

    getRecurringPaymentDates(values) {
        return this.request('GET', 'recurringPaymentDates', { ...values });
    }

    getCollateral({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'collateral',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    getCollateralDetail({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'collateral_detail',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    getAccountHasCollateralRecords({ Condition }) {
        return this.request('GET', 'history', {
            RecordType: 'collateral',
            Condition,
            Sort: 'AuditTime|DESC',
            PageNo: 1,
            RecordPerPage: 1,
            Export: false
        });
    }

    getTransactions({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'transaction',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    getTransactionDetail({ RequestId }) {
        return this.request('GET', 'history', {
            RecordType: 'transaction',
            Condition: `RequestId|EQ|${RequestId}`,
            Sort: 'AuditTime|DESC',
            PageNo: 1,
            RecordPerPage: 1,
            Export: false
        });
    }

    getPendingTransactions({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'transaction',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    approveTransaction(TransactionRequestId, currentUser, TransactionType, AccountId) {
        return this.request('POST', 'approval', {
            TransactionRequestId,
            FirmId: currentUser.FirmId,
            UserId: currentUser.UserId,
            TransactionType,
            AccountId,
            ApproveRequest: true,
            Reason: 89
        });
    }

    rejectTransaction(values: {
        TransactionRequestId: string;
        CurrentUser: UserRecord;
        TransactionType: string;
        AccountId: string;
        Reason: string;
    }) {
        return this.request('POST', 'approval', {
            TransactionRequestId: values.TransactionRequestId,
            FirmId: values.CurrentUser.FirmId,
            UserId: values.CurrentUser.UserId,
            TransactionType: values.TransactionType,
            AccountId: values.AccountId,
            ApproveRequest: false,
            Reason: values.Reason
        });
    }

    cancelTransaction(values: {
        TransactionRequestId: string;
        CurrentUser: UserRecord;
        TransactionType: string;
        AccountId: string;
        Reason: string;
    }) {
        return this.request('POST', 'cancel', {
            TransactionRequestId: values.TransactionRequestId,
            FirmId: values.CurrentUser.FirmId,
            UserId: values.CurrentUser.UserId,
            TransactionType: values.TransactionType,
            AccountId: values.AccountId,
            Reason: DeclineReasonCharEnum.NO_LONGER_REQUIRED
        });
    }

    cancelPledgeTransaction(values: {
        TransactionRequestId: string;
        CurrentUser: UserRecord;
        TransactionType: string;
        AccountId: string;
        Reason: string;
        PledgeId: string;
    }) {
        return this.request('POST', 'cancelpledgetransaction', {
            TransactionRequestId: values.TransactionRequestId,
            FirmId: values.CurrentUser.FirmId,
            UserId: values.CurrentUser.UserId,
            TransactionType: values.TransactionType,
            AccountId: values.AccountId,
            PledgeId: values.PledgeId,
            Reason: values.Reason
        });
    }

    extendSession() {
        return this.request('POST', 'session', {});
    }

    endSession() {
        this.execute('endSession');
    }

    request = async (method, endpoint, data = {}) => {
        const socket = this.socket;
        const promiseId = uuidv4();
        const promise = new Promise((resolve, reject) => {
            this.promises[promiseId] = {
                resolve,
                reject
            };
        });

        const fieldsToNumerize = [ 'CreatedAt', 'UpdatedAt' ];
        for (const field of fieldsToNumerize) {
            if (data[field]) {
                delete data[field];
            }
        }

        const msg = {
            ...data,
            UUID: promiseId,
            RequestType: method,
            RequestPattern: `${endpoint}`,
            Time: Date.now()
        };

        console.debug(`%c[WS-MESSAGING ${new Date()
            .toISOString()}]: SENDING MSG: ${JSON.stringify(msg)}`, 'color: #f44242');
        socket.write(msg);

        return promise;
    };

    execute = async (method, data = {}) => {
        const socket = this.socket;

        const msg = {
            ...data,
            type: method,
            Time: Date.now()
        };

        console.debug(`%c[WS-MESSAGING ${new Date()
            .toISOString()}]: EXECUTING MSG: ${JSON.stringify(msg)}`, 'color: #f44242');
        socket.write(msg);
    };

    inactivityLogout() {
        return this.request('POST', 'sessionTimeout');
    }
}

export class ClientMessaging extends Messaging {
    constructor(socket, hook) {
        super(socket, hook);
    }

    getUser(userId) {
        return this.request('GET', `users/${userId}`);
    }

    createHeartbeat() {
        return this.request('POST', 'heartbeat', {});
    }

    createSendRequest(payload) {
        return this.request('POST', 'send', { ...payload });
    }

    createPledgeRequest(pledgeRequest) {
        return this.request('POST', 'pledge', { ...pledgeRequest });
    }

    releasePledgeRequest(releaseRequest) {
        return this.request('POST', 'release', { ...releaseRequest });
    }

    createPledgeReclaim(pledgeRequest) {
        return this.request('POST', 'reclaim', { ...pledgeRequest });
    }

    createWithdrawalRequest(payload) {
        return this.request('POST', 'withdrawal', { ...payload });
    }

    createTransactRequest(transactRequest, approvalNeeded = false) {
        const route = approvalNeeded ? 'neworderapproval' : 'neworder';

        return this.request('POST', route, { ...transactRequest });
    }

    getTransactionsSync({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'transaction',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    getCollateralSync({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'collateral',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    acceptTermsOfService(firmId, userId, Username) {
        return this.request('PUT', 'users', {
            UserId: userId,
            FirmId: firmId,
            Username: Username,
            IsTermsOfServiceApproved: true
        });
    }

    createAddressBookEntry(obj){
        return this.request('POST', 'addresses', obj);
    }

    modifyAddressBookEntry(obj){
        return this.request('PUT', 'addresses', obj);
    }

    getAddressBookContacts({ FirmId }) {
        return this.request('GET', 'addresses', { FirmId });
    }

    deleteAddressBookRecord(obj) {
        return this.request('DELETE', 'addresses', obj);
    }

    getAccount(Account, CurCode) {
        return this.request('GET', 'accountinquiry', {
            Account,
            CurCode
        });
    }

    getDocuments({
        accountBd,
        accountId,
        token
    }) {
        return this.request('GET', 'doccenter', {
            accountBd,
            accountId,
            token
        });
    }

    getDocumentById({ accountIdList, documentId }) {
        return this.request('GET', 'doccenter', {
            accountIdList,
            documentId,
            path: 'lookup'
        });
    }

    createAPIConnectionUser(values){
        return this.request('POST', 'apiconnectionusers', values);
    }

    deleteAPIConnectionUser(apiConUserId) {
        return this.request('DELETE', 'apiconnectionusers', { APIConnectionUserId: apiConUserId });
    }

    getFirmByWalletAddress(values){
        return this.request('GET', 'firmwalletnames', values);
    }

    getFeeByOrderDetails(values){
        return this.request('GET', 'FeeAmount', values);
    }

    getWalletAddressDetails(values) {
        return this.request('GET', 'walletaddressdetails', values);
    }

    cancelTransactionOrder(values: {
        UserId: string;
        FirmId: string;
        OrderId: string;
        Reason: string;
    }) {
        return this.request('POST', 'cancelorder', {
            UserId: values.UserId,
            FirmId: values.FirmId,
            OrderId: values.OrderId,
            Reason: DeclineReasonCharEnum.NO_LONGER_REQUIRED
        });
    }
}

export class AdminMessaging extends Messaging {
    private role: string | number;

    constructor(socket, hook) {
        super(socket, hook);
    }

    setSocketRole(role: string | number) {
        this.role = role;
    }
    //Override request
    request = async (method, endpoint, data = {}) => {
        const mapping = adminRequestMapping[`${method}-${endpoint}`];
        if (mapping && !mapping.permittedRoles.includes(this.role)){
            console.warn('This role is not permmited for this request type.');

            return;
        }
        const socket = this.socket;
        const promiseId = uuidv4();
        const promise = new Promise((resolve, reject) => {
            this.promises[promiseId] = {
                resolve,
                reject
            };
        });

        const fieldsToNumerize = [ 'CreatedAt', 'UpdatedAt' ];
        for (const field of fieldsToNumerize) {
            if (data[field]) {
                delete data[field];
            }
        }

        const msg = {
            ...data,
            UUID: promiseId,
            RequestType: method,
            RequestPattern: `${endpoint}`,
            Time: Date.now()
        };

        console.debug(`%c[WS-MESSAGING ${new Date()
            .toISOString()}]: SENDING MSG: ${JSON.stringify(msg)}`, 'color: #f44242');
        socket.write(msg);

        return promise;
    };

    checkUniqueUsername(username) {
        return this.request('GET', 'checkUniqueUsername', { Username: username });
    }

    getUsers() {
        return this.request('GET', 'users');
    }

    getAdminUsers() {
        return this.request('GET', 'adminUsers');
    }

    getUser(userId) {
        return this.request('GET', `users/${userId}`);
    }

    deleteUser({ UserId }){
        return this.request('DELETE', 'users', { UserId });
    }

    updateUser(firmId, userValues) {
        return this.request('PUT', 'users', {
            ...userValues,
            FirmId: firmId
        });
    }

    createAdminUser(userValues) {
        return this.request('POST', 'users', { ...userValues });
    }

    mapPcgUser(userId, pcgId) {
        return this.request('POST', 'pcguser', {
            PCGId: pcgId,
            UserId: userId
        });
    }

    unmapPcgUser(pcgUserId) {
        return this.request('DELETE', 'pcguser', { PCGUserId: pcgUserId });
    }

    approveUserSsoPendingRequest(userValues) {
        return this.request('POST', 'user_validation', { ...userValues });
    }

    deleteUserSsoPendingRequest(userValidationId) {
        return this.request('DELETE', 'user_validation', { UserValidationId: userValidationId });
    }

    getFirms() {
        return this.request('GET', 'firms');
    }

    getAccountsByFirm = async (FirmId) => {
        return this.request('GET', 'accounts', { FirmId });
    };

    createFirm(values) {
        return this.request('POST', 'firms', {
            NeedApproval: true,
            ...values
        });
    }

    updateFirm(firmId, values) {
        const newValues = {
            ...values,
            FirmId: firmId
        };

        return this.request('PUT', 'firms', newValues);
    }

    deleteFirm(firmId) {
        return this.request('DELETE', 'firms', { FirmId: firmId });
    }

    createWithdrawalInstruction(firmId, values) {
        return this.request('POST', 'accounts_withdrawal_data', {
            ...values,
            FirmId: firmId
        });
    }

    updateWithdrawalInstruction(firmId, values) {
        const newValues = {
            ...values,
            FirmId: firmId
        };

        return this.request('PUT', 'accounts_withdrawal_data', newValues);
    }

    deleteWithdrawalInstruction(accountWithdrawalDataId) {
        return this.request('DELETE', 'accounts_withdrawal_data', { AccountWithdrawalDataId: accountWithdrawalDataId });
    }

    createFirmAccount(firmId, values) {
        return this.request('POST', 'accounts', {
            FirmId: firmId,
            ...values
        });
    }

    updateFirmAccount(firmId, accountId, values) {
        return this.request('PUT', 'accounts', {
            FirmId: firmId,
            AccountId: accountId,
            ...values
        });
    }

    setFirmLocked(FirmId, IsLocked) {
        return this.request('PUT', 'firms', {
            FirmId,
            IsLocked
        });
    }

    setDisableFirm(FirmId, IsDisabled) {
        return this.request('PUT', 'firms', {
            FirmId,
            IsDisabled
        });
    }

    setCloseAccount(FirmId, AccountId, IsClosed) {
        return this.request('PUT', 'accounts', {
            FirmId,
            AccountId,
            IsClosed
        });
    }

    setFreezeAccount(FirmId, AccountId, IsFrozen) {
        return this.request('PUT', 'accounts', {
            FirmId,
            AccountId,
            IsFrozen
        });
    }

    createUserAccount(values) {
        return this.request('POST', 'useraccount', { ...values });
    }

    deleteUserAccount(userAccountId) {
        return this.request('DELETE', 'useraccount', { UserAccountId: userAccountId });
    }

    createAccountWithdrawalRelationship(values) {
        return this.request('POST', 'accountsWithdrawalRelationships', { ...values });
    }

    updateAccountWithdrawalReltionship(values) {
        return this.request('PUT', 'accountsWithdrawalRelationships', { ...values });
    }

    deleteAccountWithdrawalRelationship(values) {
        return this.request('DELETE', 'accountsWithdrawalRelationships', { ...values });
    }

    createAcg(values) {
        return this.request('POST', 'acg', { ...values });
    }

    updateAcg(values) {
        return this.request('PUT', 'acg', { ...values });
    }

    deleteAcg(acgId) {
        return this.request('DELETE', 'acg', { ACGId: acgId });
    }

    getAlerts({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'alert',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    getAlertsExport({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'alert',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: true
        });
    }

    getApiTransactions({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'api',
            Condition,
            Sort,
            PageNo,
            RecordPerPage
        });
    }

    getClientMessages({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'clientmessage',
            Condition,
            Sort,
            PageNo,
            RecordPerPage,
            Export: false
        });
    }

    createClientMessage(values) {
        return this.request('POST', 'clientmessages', { ...values });
    }

    updateClientMessage(messageId, values) {
        return this.request('PUT', 'clientmessages', {
            ...values,
            MessageId: messageId
        });
    }

    getFirmActivity({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'activity',
            Condition,
            Sort,
            PageNo,
            RecordPerPage
        });
    }

    getUserActivity(UserId) {
        return this.request('GET', 'activities', { UserId });
    }

    resetPassword({ FirmId, UserId, Email }) {
        return this.request('PUT', 'users', {
            UserId,
            FirmId,
            ResetPassword: true,
            Email
        });
    }

    resetMFA({ FirmId, UserId, Email }) {
        return this.request('PUT', 'users', {
            UserId,
            FirmId,
            ResetMfa: true,
            Email
        });
    }

    sendExceptionResponse(selectedTransaction, FirmId, transactionAccountId, Command) {
        const {
            RequestId,
            RequesterId: UserId,
            Amount,
            Description
        } = selectedTransaction;

        const codeToPattern = {
            68: 'depositexception',
            82: 'redeemexception',
            73: 'extrecvexception',
            74: 'extsendexception'
        };

        const requestPattern = codeToPattern[selectedTransaction.TransType];

        const AccountId = transactionAccountId;

        return this.request('POST', requestPattern, {
            RequestId,
            FirmId,
            UserId,
            AccountId,
            Amount,
            Description,
            Command
        });
    }

    getAllUserRoles() {
        return this.request('GET', 'userroles');
    }

    createHeartbeat() {
        return this.request('POST', 'heartbeat', {});
    }
    getAccount(Account, CurCode) {
        return this.request('GET', 'accountinquiry', {
            Account,
            CurCode
        });
    }

    validateBdAccount(values: {
        BrokerDealerAccountNumber: string;
        FirmName: string;
        LegalEntityId: string;
    }) {
        return this.request('POST', 'brokerDealerAccountValidate', values);
    }

    createAPIConnection(values) {
        return this.request('POST', 'apiconnections', values);
    }

    updateAPIConnection(values) {
        return this.request('PUT', 'apiconnections', values);
    }

    deleteAPIConnection(apiConId) {
        return this.request('DELETE', 'apiconnections', { APIConnectionId: apiConId });
    }

    updateAPIConnectionUser(values) {
        return this.request('PUT', 'apiconnectionusers', values);
    }

    deleteAPIConnectionUser(apiConUserId) {
        return this.request('DELETE', 'apiconnectionusers', { APIConnectionUserId: apiConUserId });
    }

    updateAPIConnectionFirm(values) {
        return this.request('PUT', 'apiconnectionfirms', values);
    }

    deleteAPIConnectionFirm(apiConnFirmId) {
        return this.request('DELETE', 'apiconnectionfirms', { APIConnectionFirmId: apiConnFirmId });
    }

    createAccountOnly(values){
        return this.request('POST', 'account_only', values);
    }

    createWalletOnly(values){
        return this.request('POST', 'wallets', values);
    }

    getBankSettlementInstructions({
        Condition,
        Sort,
        PageNo,
        RecordPerPage
    }) {
        return this.request('GET', 'history', {
            RecordType: 'settlementinstructions',
            Condition,
            Sort,
            PageNo,
            RecordPerPage
        });
    }

    setNetworkBankTransactionLimit(limit: number) {
        return this.request('POST', 'transactionlimit', { TransactionLimit: limit });
    }

    setCounterparyExposureAlertLimits(counterpartyBankId: string, counterpartyBankName: string, values) {
        return this.request('POST', 'counterpartycreditrisklimit', {
            CounterPartyBankId: counterpartyBankId,
            CounterPartyBankName: counterpartyBankName,
            ...values
        });
    }

    setBank2NetworkExposureAlertLimits(values) {
        return this.request('POST', 'bankcreditrisklimit', { ...values });
    }

    setBank2NetworkLiquidityAlertLimits(values) {
        return this.request('POST', 'liquidityrisklimit', { ...values });
    }
    validateWithdrawalInstructionDescription({ Description, FirmId }) {
        return this.request('GET', 'validateWithdrawalInstructionDescription', {
            Description,
            FirmId
        });
    }
    validateBeneAddress({ Address }) {
        return this.request('GET', 'validateBeneAddress', { Address });
    }

    getFirmWithdrawalInstructions(firmId: string) {
        return this.request('GET', 'history', {
            RecordType: 'withdrawal',
            Condition: `FirmId|EQ|${firmId}`,
            Sort: 'Description|DESC',
            PageNo: 1,
            RecordPerPage: 15,
            Export: true
        });
    }
}

export default {
    ClientMessaging,
    AdminMessaging
};